import React, { useMemo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { CrescentStarIcon, StarOrnament } from './IslamicIcons';

const CONFETTI_COLORS = [
  '#fbbf24','#f59e0b','#10b981','#059669','#34d399',
  '#60a5fa','#a78bfa','#f9a8d4','#ffffff','#fde68a',
  '#6ee7b7','#93c5fd','#c4b5fd','#fca5a5','#fdba74',
];

const CELEBRATE_KEY = 'celebrated_date';

// Pure-CSS confetti particle
const Piece = ({ color, left, delay, duration, dx1, dx2, dx3, spin, size }) => (
  <div
    className="confetti-piece fixed pointer-events-none z-[200]"
    style={{
      width: size, height: size * (Math.random() > 0.5 ? 2.5 : 1),
      borderRadius: Math.random() > 0.5 ? '50%' : '2px',
      background: color,
      top: '-12px',
      left: `${left}%`,
      '--dur': `${duration}s`,
      '--delay': `${delay}s`,
      '--spin': `${spin}deg`,
      '--dx1': `${dx1}px`,
      '--dx2': `${dx2}px`,
      '--dx3': `${dx3}px`,
    }}
  />
);

/**
 * CelebrationBanner — full-screen overlay with confetti.
 * Shown when all 5 prayers are marked complete (once per day).
 */
const CelebrationBanner = ({ streak, onDismiss }) => {
  const { t } = useTranslation();
  const { isDarkMode, isRTL } = useTheme();
  const [exiting, setExiting] = useState(false);

  // Generate confetti pieces once
  const pieces = useMemo(() => (
    Array.from({ length: 60 }, (_, i) => ({
      id: i,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      left: Math.random() * 100,
      delay: Math.random() * 0.6,
      duration: 1.8 + Math.random() * 1.4,
      dx1: (Math.random() - 0.5) * 80,
      dx2: (Math.random() - 0.5) * 120,
      dx3: (Math.random() - 0.5) * 60,
      spin: (Math.random() > 0.5 ? 1 : -1) * (360 + Math.random() * 360),
      size: 6 + Math.random() * 8,
    }))
  ), []);

  // Auto-dismiss after 4s
  useEffect(() => {
    const timer = setTimeout(() => { setExiting(true); setTimeout(onDismiss, 380); }, 4000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  const msg = t('home.allPrayersDoneMsg').replace('{{streak}}', streak);

  return (
    <>
      {/* Confetti layer */}
      <div className="fixed inset-0 pointer-events-none z-[200] overflow-hidden">
        {pieces.map(p => <Piece key={p.id} {...p} />)}
      </div>

      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[201] bg-black/55 backdrop-blur-sm flex items-center justify-center px-6"
        onClick={() => { setExiting(true); setTimeout(onDismiss, 380); }}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {/* Card */}
        <div
          className={`w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl pointer-events-none ${
            exiting ? 'celebration-exit' : 'celebration-appear'
          } ${isDarkMode
            ? 'bg-gradient-to-br from-[#0f172a] via-[#1e3a5f] to-[#0f2744] border border-amber-500/20'
            : 'bg-gradient-to-br from-[#0f172a] via-[#1a365d] to-[#0f2744] border border-amber-500/30'
          }`}
        >
          {/* Top decorative strip */}
          <div className="h-1.5 w-full bg-gradient-to-r from-amber-400 via-emerald-400 to-amber-400" />

          <div className="px-6 pt-7 pb-7 text-center">
            {/* Islamic ornament */}
            <div className="flex justify-center mb-4">
              <div className="relative">
                <div className="w-20 h-20 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center">
                  <CrescentStarIcon size={36} color="#fbbf24" />
                </div>
                {/* Orbiting stars */}
                {[0,60,120,180,240,300].map((deg, i) => {
                  const rad = deg * Math.PI / 180;
                  return (
                    <div key={i} className="absolute w-4 h-4 flex items-center justify-center"
                      style={{
                        top: `calc(50% + ${Math.sin(rad) * 42}px - 8px)`,
                        left: `calc(50% + ${Math.cos(rad) * 42}px - 8px)`,
                      }}>
                      <StarOrnament size={8} color="#fbbf24" />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Headline */}
            <h2 className="text-3xl font-black text-amber-400 mb-1" style={{ letterSpacing: '-0.02em' }}>
              {t('home.allPrayersDone')}
            </h2>
            <p className="text-white text-lg font-semibold mb-2">
              {t('home.allPrayersDoneSubtitle')}
            </p>
            {streak > 0 && (
              <p className="text-white/60 text-sm">
                {msg}
              </p>
            )}

            {/* Auto-dismiss progress bar */}
            <div className="mt-6 h-1 bg-white/15 rounded-full overflow-hidden">
              <div
                className="h-full bg-amber-400/70 rounded-full"
                style={{ animation: 'progress-grow 4s linear forwards' }}
              />
            </div>
            <p className="text-white/35 text-xs mt-2">{t('home.tapToDismiss')}</p>
          </div>
        </div>
      </div>
    </>
  );
};

/**
 * Hook — returns showCelebration and dismiss handler.
 * Tracks once-per-day state in localStorage.
 */
export const useCelebration = (completedCount, totalPrayers, streak) => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (completedCount !== totalPrayers || totalPrayers === 0) return;
    const today = new Date().toISOString().slice(0, 10);
    if (localStorage.getItem(CELEBRATE_KEY) === today) return;
    // Small delay so the last prayer check-mark animation plays first
    const t = setTimeout(() => {
      localStorage.setItem(CELEBRATE_KEY, today);
      setShow(true);
    }, 600);
    return () => clearTimeout(t);
  }, [completedCount, totalPrayers]);

  return { showCelebration: show, dismissCelebration: () => setShow(false) };
};

export default CelebrationBanner;
